import { formatDate, DATE_FORMATS } from '@frontend/mwm-ui'
import { useState } from 'react'
import { FaUnity } from 'react-icons/fa'
import { MdContentCopy, MdEdit, MdOutlineFileDownload } from 'react-icons/md'
import { useParams } from 'react-router-dom'

import type { ChangeEvent, ReactElement } from 'react'
import type { IPageContainer } from '~/types/page-container'
import type { IPageContainerFormValues } from '~/types/template'

import PageContainerInfo from './Info'

import { getFile } from '~/api/file'
import Badge from '~/components/Badge'
import { PageContainerBadge } from '~/components/Badges'
import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import TextInput from '~/components/Inputs/Basic/TextInput'
import UserCard from '~/components/UserCard'
import { useUserDetailsQuery } from '~/queries/users'
import { PAGE_CONTAINER_STATUS } from '~/types/page-container'
import { downloadFromUrl } from '~/utils/file'
import { copyText } from '~/utils/navigator'
import { capitalize } from '~/utils/string'
import { useTemplateDetailsQuery, useTemplateVersionsQuery } from '~/views/DynamicScreens/Templates/queries'

import './styles.scss'

interface Props {
  onTemplateVersionChange?: (version: string) => void
  onTitleChange?: (e: ChangeEvent<HTMLInputElement>) => void
  pageContainer?: IPageContainer
  status: PAGE_CONTAINER_STATUS
  title: string
  values?: IPageContainerFormValues
}

const DetailsHeader = ({
  onTemplateVersionChange,
  onTitleChange,
  pageContainer,
  status,
  title,
  values,
}: Props): ReactElement => {
  const { pageContainerId } = useParams<{ pageContainerId: string }>()

  const [isTitleOnEdit, setIsTitleOnEdit] = useState(false)

  const { data: user } = useUserDetailsQuery(pageContainer?.updated_by)

  const { data: templateData } = useTemplateDetailsQuery(
    {
      deviceType: pageContainer?.device_type as string,
      isUnity: pageContainer?.is_unity as boolean,
      key: pageContainer?.template_key as string,
      version: pageContainer?.template_version as string,
    },
    { enabled: !!pageContainer }
  )

  const { data: templateVersions } = useTemplateVersionsQuery(values?.templateKey as string, {
    enabled: !!onTemplateVersionChange && !!values?.templateKey,
  })

  const templateVersionOptions =
    templateVersions
      ?.filter(
        ({ device_type, is_unity }) => device_type === values?.selectedApp?.device_type && is_unity === values.isUnity
      )
      .map(({ version }) => ({ label: version, value: version })) || []

  const workspaceFileId = templateData?.platform_dependant_properties?.workspace_file_id

  const isEditable = pageContainer?.status === PAGE_CONTAINER_STATUS.DRAFT || !pageContainer

  const handleIsTitleOnEdit = (isOnEdit: boolean) => () => setIsTitleOnEdit(isOnEdit)

  const handleDownloadWorkspace = async (fileId: string) => {
    const { download_url } = await getFile({ fileId })
    downloadFromUrl(download_url)
  }

  return (
    <div className="page-container-details-header">
      <div className="page-container-details-header__top">
        <div className="title-status-container">
          {isTitleOnEdit && !!onTitleChange ? (
            <TextInput
              autoFocus
              defaultValue={title}
              name="title"
              onBlur={handleIsTitleOnEdit(false)}
              onChange={onTitleChange}
              wrapperClassName="title-input"
            />
          ) : (
            <div className="title-button" onClick={handleIsTitleOnEdit(true)}>
              <div className="title-value-container">{title}</div>
              {isEditable && (
                <div className="edit-pen-icon">
                  <MdEdit size={16} />
                </div>
              )}
            </div>
          )}
          {pageContainerId ? (
            <Button
              className="copy-button"
              ghost
              onClick={() => copyText(pageContainerId, 'The page container ID has just been copied to the clipboard')}
              type="button"
            >
              <MdContentCopy size={16} />
            </Button>
          ) : null}
          {!!workspaceFileId ? (
            <Button className="download-button" ghost onClick={() => handleDownloadWorkspace(workspaceFileId)}>
              <MdOutlineFileDownload size={16} />
            </Button>
          ) : null}
          <PageContainerBadge size="large" value={status} />
        </div>
        {pageContainer?.template_key && pageContainer.template_version ? (
          <div className="template-version-container">
            <div className="template-info-container">
              <span className="template-info-container__label">Template:</span>{' '}
              <Badge className="template-info-container__badge" value={pageContainer.template_key} />
              <Button
                className="copy-button"
                ghost
                onClick={() =>
                  copyText(pageContainer.template_key, 'The template key has just been copied to the clipboard')
                }
                size="small"
                type="button"
              >
                <MdContentCopy size={16} />
              </Button>
            </div>
            <div className="template-info-container">
              <span className="template-info-container__label">Version:</span>{' '}
              <Badge className="template-info-container__badge" value={pageContainer.template_version} />
            </div>
            {pageContainer.is_unity ? (
              <div className="template-info-container">
                <FaUnity size={16} />
              </div>
            ) : null}
          </div>
        ) : null}
        {onTemplateVersionChange ? (
          <div className="template-version-select-input-container">
            <SelectInput
              label="Template version"
              onChange={(option) => onTemplateVersionChange((option as IOption).value)}
              options={templateVersionOptions}
              value={{ label: values?.templateVersion, value: values?.templateVersion }}
            />
          </div>
        ) : null}
      </div>
      <div className="page-container-details-header__bottom">
        <div>
          {pageContainer?.capabilities?.length ? (
            <PageContainerInfo infos={pageContainer.capabilities} title="Capabilities" />
          ) : null}
          {pageContainer?.requirements?.length ? (
            <PageContainerInfo infos={pageContainer.requirements} title="Requirements" />
          ) : null}
          {pageContainer?.navigation_target_outputs?.length ? (
            <PageContainerInfo infos={pageContainer.navigation_target_outputs} title="Navigation targets" />
          ) : null}
        </div>
        {user && pageContainer?.updated_at && (
          <div>
            <UserCard
              backgroundColor="default"
              label="Last update by"
              labelPosition="left"
              name={user.name ? capitalize(user.name) : user.user_id}
              picture={user.picture}
              size="medium"
              subInfo={formatDate(pageContainer.updated_at, DATE_FORMATS.DEFAULT_WITH_HOURS)}
            />
          </div>
        )}
      </div>
    </div>
  )
}
export default DetailsHeader
